import React, { useState } from 'react';
import { Container, Form, Button, ProgressBar, Dropdown, DropdownButton, Tab, Alert, Row, Col, Nav } from 'react-bootstrap';
import axiosInstance from '../components/AxiosInstance';
import { API_URL } from '../Config';
import axios from 'axios';

// Password strength check function
const checkPasswordStrength = (password) => {
    const lengthCriteria = password.length >= 8;
    const upperCaseCriteria = /[A-Z]/.test(password);
    const lowerCaseCriteria = /[a-z]/.test(password);
    const numberCriteria = /[0-9]/.test(password);
    const specialCharCriteria = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const strength = [lengthCriteria, upperCaseCriteria, lowerCaseCriteria, numberCriteria, specialCharCriteria].filter(Boolean).length;

    if (strength === 5) return 'success';
    if (strength === 4) return 'info';
    if (strength === 3) return 'warning';
    return 'danger';
};

const Password = () => {

    const [confirmPassword, setConfirmPassword] = useState('');
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
    });

    const [sqa, setSqa] = useState({
        qaId: '',
        answer: '',
    })

    const [securityQuestions, setSecurityQuestions] = useState([]);
    const [selectedQuestion, setSelectedQuestion] = useState('');

    const [passwordStrength, setPasswordStrength] = useState('danger');
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    // States for security details form    
    const [securityAnswer, setSecurityAnswer] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsSubmitting(true);
        if (formData.newPassword !== confirmPassword) {
            setError('New password & confirm password do not match.');
            setIsSubmitting(false);
            return;
        }
        // Handle the password change submission here
        try {
            var url = API_URL + "auth/updatepassword";
            const response = await axiosInstance.post(url, formData);
            setSuccessMessage(response.data.message);
            setFormData({
                oldPassword: '',
                newPassword: '',
            });
            setConfirmPassword({
                confirmPassword: '',
            });
        } catch (error) {
            if (error.response) {
                // The server responded with an error
                setError("" + error.response.data.error + "");
            }
        }
        setIsSubmitting(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === 'newPassword') {
            setPasswordStrength(checkPasswordStrength(value));
        }
    };

    const getSecurityQuestions = async () => {
        try {
            var url = API_URL + "auth/getsecurityquestions";
            const res = await axios.get(url);
            if (res.data && res.data.sqa) {
                setSecurityQuestions(res.data.sqa);  // Adjust based on API structure                
            } else {
                setError('Invalid user data structure.');
            }

        } catch (err) {
            setError("Unable to fetch profile data. Please try again!");

        }
    };

    const handleSelectQuestion = (question) => {
        const selected = securityQuestions.find(q => q.questions === (question));
        setSelectedQuestion(selected.questions);
    };

    const saveQuestions = async (e) => {
        e.preventDefault();
        setError("");
        setIsSubmitting(true);
        const questionId = securityQuestions.find(q => q.questions === (selectedQuestion));
        setSqa({
            qaId: questionId.security_qa_id,
            answer: securityAnswer,
        });

        sqa.qaId = questionId.security_qa_id;
        sqa.answer = securityAnswer;

        try {
            var url = API_URL + "auth/updatesqa";
            const response = await axiosInstance.post(url, sqa);
            setSuccessMessage(response.data.message);
        } catch (error) {
            // Handle any errors
            if (error.response) {
                // The server responded with an error
                console.error("Error response:", error.response.data);
                setError("An error occurred. Please try again.");

            } else if (error.request) {
                // The request was made but no response was received
                console.error("Error request:", error.request);
                setError("No response from the server. Please try again later.");
            } else {
                // Something else caused the error
                console.error("Error:", error.message);
                setError("An unknown error occurred. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelQuestions = () => {

    };

    return (
        <div>
            <Container className="collaborate-section rounded" >
                <div className="text-center">
                    <span className="highlight-text">
                        Password & Security details
                    </span>
                </div>
                <br />
                {successMessage && <Alert variant="success">{successMessage}</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}
                <br />
                <Tab.Container id="change-password-tabs" defaultActiveKey="1">
                    <Row>
                        <Col sm={3}>
                            <Nav variant="pills" className="flex-column">
                                <Nav.Item>
                                    <Nav.Link eventKey="1">Change Password</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="2" onClick={getSecurityQuestions} >Security details</Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Col>
                        <Col sm={9}>
                            <Tab.Content>
                                <Tab.Pane eventKey="1">
                                    <Alert variant='info'>
                                        <p>
                                            Change your account password below. Password strength is important for your account; we strongly recommend you to follow below guidelines while creating a new password. Follow the tips below to keep your password safe.
                                        </p>
                                        <p> <b>Note:</b> If you change your password, you will end your current session and re-login again with new password.</p>
                                    </Alert>
                                    <Form onSubmit={handleSubmit}>
                                        {/* Old Password Input */}
                                        <Form.Group controlId="formOldPassword">
                                            <Form.Label>Old Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="oldPassword"
                                                placeholder="Enter old password"
                                                value={formData.oldPassword}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>

                                        {/* New Password Input with Strength Indicator */}
                                        <Form.Group controlId="formNewPassword">
                                            <Form.Label>New Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="newPassword"
                                                placeholder="Enter new password"
                                                value={formData.newPassword}
                                                onChange={handleChange}
                                                isInvalid={passwordStrength === 'danger'}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        {/* Confirm Password */}
                                        <Form.Group controlId="formConfirmPassword">
                                            <Form.Label>Confirm New Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="confirmPassword"
                                                placeholder="Confirm new password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                        <br />
                                        {/* Password Strength Progress Bar */}
                                        <ProgressBar animated
                                            now={formData.newPassword.length * 10}
                                            label={`${formData.newPassword.length * 10}%`}
                                            variant={passwordStrength}
                                            className="mb-3"
                                        />

                                        {/* Submit Button */}
                                        <Button variant="primary" type="submit" onClick={handleSubmit} disabled={isSubmitting || passwordStrength === 'danger'}>
                                            {isSubmitting ? "Changing Password..." : "Change Password"}
                                        </Button>
                                        <br /><br />
                                        <Alert variant='info'>
                                            <p>
                                                <h4>Password Safety Guidelines</h4>
                                                <ul>
                                                    <li><strong>Create Strong Passwords:</strong>
                                                        <ul>
                                                            <li>Use at least 8 characters. Include a mix of uppercase letters, lowercase letters, numbers, and special characters.</li> <li>Avoid easily guessable passwords like "password123", "admin", or "qwerty".</li>
                                                            <li>Do not use personal information such as your name, birthdate, or family names.</li>
                                                        </ul>
                                                    </li>
                                                    <li><strong>Use Unique Passwords:</strong>
                                                        <ul>
                                                            <li>Never use the same password across multiple sites.</li>
                                                            <li>Use a reputable password manager to generate and store unique passwords for each of your accounts.</li>
                                                        </ul> </li>
                                                    <li><strong>Regular Updates:</strong>
                                                        <ul>
                                                            <li>Update your passwords periodically, especially for critical accounts.</li>
                                                            <li>Regularly check your account activity for unauthorized access and update your password if you notice anything suspicious.</li>
                                                        </ul>
                                                    </li>
                                                    <li><strong>Enable Two-Factor Authentication (2FA):</strong>
                                                        <ul>
                                                            <li>Enable 2FA whenever possible. This adds an extra layer of security, requiring not just a password but also a second form of verification.</li>
                                                        </ul>
                                                    </li>
                                                    <li><strong>Secure Storage:</strong>
                                                        <ul>
                                                            <li>Avoid writing down passwords or storing them in plain text files on your computer.</li>
                                                            <li>Use encrypted storage or a secure password manager if you need to store passwords.</li>
                                                        </ul>
                                                    </li>
                                                    <li><strong>Beware of Phishing:</strong>
                                                        <ul>
                                                            <li>Always verify the URL before entering your password. Be cautious of emails or messages asking for your login information.</li>
                                                            <li>Stay informed about common phishing techniques and learn how to recognize suspicious emails or websites.</li>
                                                        </ul>
                                                    </li>
                                                    <li><strong>Example of a Strong Password:</strong>
                                                        <ul>
                                                            <li>An example of a strong password might be: <code>aB1$eD2!gH3@kLmN</code></li>
                                                        </ul>
                                                    </li>
                                                    <li><strong>Using a Password Manager:</strong>
                                                        <ul>
                                                            <li>Tools like LastPass, 1Password, or Bitwarden can help you generate and manage complex passwords securely. They also offer features like auto-filling passwords, secure notes, and monitoring for potential breaches.</li>
                                                        </ul>
                                                    </li>
                                                    <li><strong>Two-Factor Authentication Apps:</strong>
                                                        <ul> <li>Apps like Google Authenticator, Authy, or Microsoft Authenticator provide easy-to-use 2FA services to add an extra layer of security.</li>
                                                        </ul>
                                                    </li>
                                                </ul>
                                            </p>
                                        </Alert>
                                    </Form>
                                </Tab.Pane>
                                <Tab.Pane eventKey="2">
                                    <Form>
                                        {/* Security Question Dropdown */}
                                        <Form.Group controlId="formSecurityQuestion">
                                            <Form.Label>Security Question</Form.Label>
                                            <DropdownButton
                                                id="dropdown-security-question"
                                                title={selectedQuestion || 'Select a security question'}
                                                onSelect={handleSelectQuestion}
                                                variant="info"
                                            >
                                                {securityQuestions.map((item) => (
                                                    <Dropdown.Item key={item.security_qa_id} eventKey={item.questions}>
                                                        {item.questions}
                                                    </Dropdown.Item>
                                                ))}
                                            </DropdownButton>
                                        </Form.Group>

                                        {/* Security Answer Input */}
                                        <Form.Group controlId="formSecurityAnswer">
                                            <Form.Label>Answer to Security Question</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter your answer"
                                                value={securityAnswer}
                                                onChange={(e) => setSecurityAnswer(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                        <br />
                                        <Button variant="light" type="submit" onClick={cancelQuestions} disabled={isSubmitting}>
                                            {isSubmitting ? "Cancelling changes..." : "Cancel Changes"}
                                        </Button>
                                        <Button variant="primary" type="submit" onClick={saveQuestions} disabled={isSubmitting}>
                                            {isSubmitting ? "Saving changes..." : "Save Changes"}
                                        </Button>
                                    </Form>
                                </Tab.Pane>
                            </Tab.Content>
                        </Col>
                    </Row>
                </Tab.Container>
            </Container>
        </div>
    );
};

export default Password;
